/* =============================================
   BLOG POSTS GRID PRO — JS (deferred)
   Horizontal snap carousel + black dot pagination + 7s autoplay
   Storefront GraphQL pagination: loads ALL matching articles
   beyond the 50-item Liquid limit (Plan C).
   No third-party libraries, no inline onclick.
   ============================================= */
;(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var PAGE_SIZE = 50;

  /* ---------- Storefront GraphQL loading (Plan C) ---------- */

  function parseMetafield(raw) {
    // s.model_metafield is "namespace.key"
    if (!raw || raw.indexOf('.') === -1) return null;
    var parts = raw.split('.');
    return { namespace: parts[0], key: parts[1] };
  }

  /* ---------- shared project metaobject ----------
     文章上的 metaobject_reference（默认 custom.project）指向一条 rvrn_project entry，
     同一订单项目的多篇文章共用它。区块只需新增一个「Project reference metafield」设置：
     项目字段按「旧 metafield 的 key」自动对应，映射表与 snippets/rvrn_mf_value.liquid 一致，
     不额外增加 4 个设置项。
     读取时项目优先、旧 metafield 回落；未配置项目时整段不参与查询，行为与改造前一致。 */
  // rvrn_project 定义的全部 33 个字段 key —— 与线上定义读回逐项一致。
  // 选择集是硬编码的：不在这里的字段，Storefront 根本不会取回。
  var PROJECT_FIELDS = [
    'year', 'make', 'model', 'trim', 'combined', 'region', 'wheel', 'rep',
    'wheel_size', 'wheel_size_front', 'wheel_size_rear',
    'center_bore', 'pcd', 'brake_setup', 'customer_name',
    'title', 'project_code', 'construction',
    'product', 'hero_image', 'gallery',
    'wheel_gallery_article', 'vehicle_gallery_article', 'car_event_article',
    'build_notes', 'build_summary', 'fitment_notes', 'owner_feedback',
    'vehicle_color', 'finish_details', 'finishes', 'finish_image', 'case_product'
  ];

  // 字段 key -> rvrn_project 定义里的类型名（代码侧的 schema 参照）。
  // 与 PROJECT_FIELDS 一一对应；用于判断原始值能否直接交给 UI。
  var PROJECT_FIELD_TYPES = {
    title: 'single_line_text_field',
    project_code: 'single_line_text_field',
    wheel: 'single_line_text_field',
    construction: 'single_line_text_field',
    year: 'number_integer',
    make: 'single_line_text_field',
    model: 'single_line_text_field',
    trim: 'single_line_text_field',
    combined: 'single_line_text_field',
    region: 'single_line_text_field',
    wheel_size: 'single_line_text_field',
    wheel_size_front: 'single_line_text_field',
    wheel_size_rear: 'single_line_text_field',
    center_bore: 'single_line_text_field',
    pcd: 'single_line_text_field',
    brake_setup: 'single_line_text_field',
    rep: 'single_line_text_field',
    customer_name: 'single_line_text_field',
    product: 'product_reference',
    hero_image: 'file_reference',
    gallery: 'list.file_reference',
    wheel_gallery_article: 'article_reference',
    vehicle_gallery_article: 'article_reference',
    car_event_article: 'article_reference',
    build_notes: 'multi_line_text_field',
    build_summary: 'multi_line_text_field',
    fitment_notes: 'multi_line_text_field',
    owner_feedback: 'multi_line_text_field',
    vehicle_color: 'single_line_text_field',
    finish_details: 'multi_line_text_field',
    finishes: 'list.metaobject_reference',
    finish_image: 'file_reference',
    case_product: 'product_reference'
  };

  // 旧 metafield key -> rvrn_project 字段 key
  var PROJECT_ALIAS = {
    event_vehicle_make_model: 'combined',
    vehicle_make_model: 'combined',
    vehicle_make: 'make',
    customer_location: 'region',
    wheel_model: 'wheel',
    representative: 'rep',
    event_wheel_size: 'wheel_size',
    front_wheel_size: 'wheel_size_front',
    rear_wheel_size: 'wheel_size_rear',
    center_bore: 'center_bore',
    pcd: 'pcd',
    brake_setup: 'brake_setup',
    event_customer_name: 'customer_name',
    case_product: 'case_product'
  };

  function projectAlias(field) {
    return 'p' + field.charAt(0).toUpperCase() + field.slice(1);
  }

  function projectFrag(projectMf) {
    if (!projectMf) return '';
    var sel = 'handle ';
    PROJECT_FIELDS.forEach(function (f) {
      sel += projectAlias(f) + ': field(key: "' + f + '") { value } ';
    });
    return 'mfProject: metafield(namespace: "' + projectMf.namespace + '", key: "' + projectMf.key +
      '") { reference { ... on Metaobject { ' + sel + '} } } ';
  }

  // 类型保护：list.* 与 *_reference 的 value 不是可直接渲染的文本
  // （list 是 JSON 数组字符串，如 ["gid://shopify/Metaobject/..."];
  //   *_reference 是 GID，如 ["gid://shopify/MediaImage/..."]），
  // 直接交给 UI 会露出 GID。这类字段一律按「取不到」处理，除非调用方显式传 raw。
  function isOpaqueFieldType(field) {
    var t = PROJECT_FIELD_TYPES[field] || '';
    return t.indexOf('list.') === 0 || /_reference$/.test(t);
  }

  // 共享优先：命中项目字段就返回，否则返回空串由调用方回落到旧 metafield。
  // opts.raw === true 时跳过类型保护，由调用方自己处理原始值。
  function projectValue(article, legacyMf, opts) {
    if (!legacyMf || !legacyMf.key) return '';
    var field = PROJECT_ALIAS[legacyMf.key];
    if (!field) return '';
    if (isOpaqueFieldType(field) && !(opts && opts.raw === true)) return '';
    var ref = article.mfProject && article.mfProject.reference;
    var f = ref && ref[projectAlias(field)];
    return f && f.value != null ? String(f.value) : '';
  }

  function buildArticleQuery(tagFilter) {
    // Storefront article search only supports tag: filtering on the articles
    // connection. Blog scoping is done via blogByHandle (see fetchArticles).
    return tagFilter ? 'tag:' + tagFilter : null;
  }

  // 卡片所需 node 级选择集。articleFields() 与「按 handle 取回置顶文章」共用，避免两处重复。
  function articleNodeSelection(modelMf, finishMf, titleMf, badgeMf, projectMf) {
    // metafield selection fragments (only if configured)
    var modelFrag = '';
    var finishFrag = '';
    var titleFrag = '';
    var badgeFrag = '';
    if (modelMf) {
      modelFrag =
        'metafield1: metafield(namespace: "' + modelMf.namespace + '", key: "' + modelMf.key + '") { value } ';
    }
    if (finishMf) {
      finishFrag =
        'metafield2: metafield(namespace: "' + finishMf.namespace + '", key: "' + finishMf.key + '") { value } ';
    }
    if (titleMf) {
      titleFrag =
        'titleMetafield: metafield(namespace: "' + titleMf.namespace + '", key: "' + titleMf.key + '") { value } ';
    }
    if (badgeMf) {
      badgeFrag =
        'badgeMetafield: metafield(namespace: "' + badgeMf.namespace + '", key: "' + badgeMf.key + '") { value } ';
    }
    return (
      'handle title excerpt image { url altText } ' +
      'tags onlineStoreUrl ' +
      modelFrag +
      finishFrag +
      titleFrag +
      badgeFrag +
      projectFrag(projectMf)
    );
  }

  function articleFields(modelMf, finishMf, titleMf, badgeMf, projectMf) {
    return (
      'edges { cursor node { ' +
      articleNodeSelection(modelMf, finishMf, titleMf, badgeMf, projectMf) +
      ' } } ' +
      'pageInfo { hasNextPage endCursor }'
    );
  }

  function gqlRequest(section, query, variables) {
    var token = section.getAttribute('data-storefront-token');
    var domain = section.getAttribute('data-shop-domain');
    var version = section.getAttribute('data-api-version') || '2024-01';
    if (!token || !domain) {
      // No token configured — fall back to server-rendered cards only.
      return Promise.reject(new Error('storefront_token_not_configured'));
    }
    return fetch('https://' + domain + '/api/' + version + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (res) {
      if (!res.ok) throw new Error('graphql_http_' + res.status);
      return res.json();
    });
  }

  function fetchArticles(section, blogHandle, tagFilter, cursor) {
    var modelMf = parseMetafield(section.getAttribute('data-model-metafield'));
    var finishMf = parseMetafield(section.getAttribute('data-finish-metafield'));
    var titleMf = parseMetafield(section.getAttribute('data-title-metafield'));
    var badgeMf = parseMetafield(section.getAttribute('data-badge-metafield'));
    var projectMf = parseMetafield(section.getAttribute('data-project-metafield'));

    // Blog scoping is done via blogByHandle (Storefront's articles(query:)
    // does NOT support a reliable "blog:" filter — it matches across blogs).
    // Optional tag filtering is passed to the articles connection as query:"tag:...".
    var tagQuery = buildArticleQuery(tagFilter);
    var fields = articleFields(modelMf, finishMf, titleMf, badgeMf, projectMf);

    var query =
      'query ($first: Int!, $after: String, $handle: String!, $q: String) { ' +
      'blogByHandle(handle: $handle) { ' +
      'articles(first: $first, after: $after, query: $q, sortKey: PUBLISHED_AT, reverse: true) { ' +
      fields +
      ' } } }';

    var variables = {
      first: PAGE_SIZE,
      after: cursor || null,
      handle: blogHandle,
      q: tagQuery
    };

    return gqlRequest(section, query, variables).then(function (json) {
      if (json.errors && json.errors.length) {
        throw new Error(json.errors[0].message);
      }
      var blog = json.data && json.data.blogByHandle;
      if (!blog) {
        throw new Error('Storefront blog not found: ' + blogHandle);
      }
      return blog.articles;
    });
  }

  function escAttr(s) {
    return (s || '').replace(/"/g, '&quot;');
  }
  function escHtml(s) {
    return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function cleanBadgeValue(v) {
    var s = (v == null ? '' : v).toString().trim();
    // Storefront list metafields return JSON array strings like ["2-Piece"],
    // strip brackets/quotes so the raw label shows.
    if (s.charAt(0) === '[' && s.charAt(s.length - 1) === ']') {
      try {
        var arr = JSON.parse(s);
        if (Array.isArray(arr) && arr.length) s = arr.join(', ');
      } catch (e) {
        s = s.replace(/^\[|\]$/g, '').replace(/"/g, '').trim();
      }
    }
    return s;
  }

  function cardMarkup(article, modelMf, finishMf, titleMf, badgeMf, finishLabel, readLabel) {
    // Mirror the server-rendered Liquid card BEM markup exactly.
    var handle = article.handle;
    var rawTitle = (article.title || '').toString();
    var title = rawTitle;
    var projTitle = projectValue(article, titleMf);
    if (projTitle) {
      title = projTitle;
    } else if (titleMf && article.titleMetafield && article.titleMetafield.value) {
      title = article.titleMetafield.value.toString();
    }
    var excerpt = (article.excerpt || '').toString();
    var url = article.onlineStoreUrl || '#';
    var img = article.image && article.image.url ? article.image.url : '';
    var imgAlt = (article.image && article.image.altText) || title;
    // Badge: only shown when a badge metafield is configured AND has a
    // non-empty value. No fallback to tags — if the metafield is empty/null,
    // the badge is hidden entirely.
    var badge = '';
    if (badgeMf && article.badgeMetafield && article.badgeMetafield.value) {
      badge = cleanBadgeValue(article.badgeMetafield.value);
    }

    // 共享项目优先，取不到再回落文章自身的 metafield（老数据 / 单页覆盖）。
    var model = projectValue(article, modelMf);
    var finish = projectValue(article, finishMf);
    if (!model && article.metafield1 && article.metafield1.value) model = article.metafield1.value;
    if (!finish && article.metafield2 && article.metafield2.value) finish = article.metafield2.value;

    var html =
      '<article class="rvrn-posts-gallery-grid__card" data-article-handle="' + escAttr(handle) + '">' +
        '<div class="rvrn-posts-gallery-grid__media">';
    if (badge) {
      html += '<span class="rvrn-posts-gallery-grid__badge">' + escHtml(badge) + '</span>';
    }
    if (img) {
      html +=
        '<a href="' + escAttr(url) + '" class="rvrn-posts-gallery-grid__media-link" aria-label="' + escAttr(title) + '" tabindex="-1">' +
          '<div class="rvrn-media rvrn-media--ratio-3-2" style="--rvrn-media-fit: cover; --rvrn-media-position: center center; --rvrn-media-mobile-position: center center;">' +
            '<img src="' + escAttr(img) + '" alt="' + escAttr(imgAlt) + '" class="rvrn-media__image rvrn-media__image--desktop" loading="lazy">' +
          '</div>' +
        '</a>';
    }
    html +=
        '</div>' +
        '<div class="rvrn-posts-gallery-grid__body">' +
          '<h3 class="rvrn-posts-gallery-grid__title">' + escHtml(title) + '</h3>';
    if (model) {
      html += '<div class="rvrn-posts-gallery-grid__subtitle">' + escHtml(model) + '</div>';
    }
    if (finish) {
      html +=
          '<div class="rvrn-posts-gallery-grid__finish">' +
            '<span class="rvrn-posts-gallery-grid__finish-label">' + escHtml(finishLabel || 'Finish Specification') + '</span>' +
            '<span class="rvrn-posts-gallery-grid__finish-value">' + escHtml(finish) + '</span>' +
          '</div>';
    }
    html +=
          '<div class="rvrn-posts-gallery-grid__footer">' +
            '<a class="rvrn-btn rvrn-btn--dark rvrn-btn--small rvrn-posts-gallery-grid__read-btn" href="' + escAttr(url) + '" aria-label="' + escAttr(readLabel) + '">' +
              '<span class="rvrn-btn__label">' + escHtml(readLabel) + '</span>' +
            '</a>' +
          '</div>' +
        '</div>' +
      '</article>';
    return html;
  }

  function loadAllViaStorefront(section) {
    var track = section.querySelector('[data-track]');
    if (!track) return;

    var modelMf = parseMetafield(section.getAttribute('data-model-metafield'));
    var finishMf = parseMetafield(section.getAttribute('data-finish-metafield'));
    var titleMf = parseMetafield(section.getAttribute('data-title-metafield'));
    var badgeMf = parseMetafield(section.getAttribute('data-badge-metafield'));
    var projectMf = parseMetafield(section.getAttribute('data-project-metafield'));
    var readLabel = (section.getAttribute('data-read-btn-label') || 'Read More').toString().trim() || 'Read More';
    var maxArticles = parseInt(section.getAttribute('data-max-articles'), 10) || 0;
    var finishLabel = section.getAttribute('data-finish-label');

    // Source pairs: [primary, additional]. Additional only used to fill gaps.
    var handles = [
      section.getAttribute('data-blog-handle'),
      section.getAttribute('data-blog-handle-2')
    ];
    var tags = [
      section.getAttribute('data-tag-filter'),
      section.getAttribute('data-tag-filter-2')
    ];

    var existing = {};
    track.querySelectorAll('.rvrn-posts-gallery-grid__card').forEach(function (c) {
      var h = c.getAttribute('data-article-handle');
      if (h) existing[h] = true;
    });

    function currentCount() {
      return track.querySelectorAll('.rvrn-posts-gallery-grid__card').length;
    }

    var guard = 0;

    // Sequentially drain each source until maxArticles reached or sources exhausted.
    function drainSource(srcIndex) {
      if (srcIndex >= handles.length) {
        init(section);
        return;
      }
      var blogHandle = handles[srcIndex];
      var tagFilter = tags[srcIndex];
      if (!blogHandle) {
        drainSource(srcIndex + 1);
        return;
      }
      var cursor = null;

      function page() {
        if (guard++ > 40) return; // safety: never loop forever
        if (maxArticles && currentCount() >= maxArticles) {
          init(section);
          return;
        }
        fetchArticles(section, blogHandle, tagFilter, cursor).then(function (conn) {
          if (!conn) {
            drainSource(srcIndex + 1);
            return;
          }
          var frag = document.createDocumentFragment();
          conn.edges.forEach(function (edge) {
            var node = edge.node;
            if (existing[node.handle]) return; // skip dup across both sources
            if (maxArticles && currentCount() + frag.childNodes.length >= maxArticles) return; // cap reached
            existing[node.handle] = true;
            var wrap = document.createElement('div');
            wrap.innerHTML = cardMarkup(node, modelMf, finishMf, titleMf, badgeMf, finishLabel, readLabel);
            frag.appendChild(wrap.firstChild);
          });
          if (frag.childNodes.length) track.appendChild(frag);

          if (conn.pageInfo && conn.pageInfo.hasNextPage) {
            cursor = conn.pageInfo.endCursor;
            page();
          } else {
            // This source exhausted — move to the next (additional) source.
            drainSource(srcIndex + 1);
          }
        }).catch(function (err) {
          if (window.console) console.warn('[rvrn-posts-gallery-grid] source skipped:', blogHandle, err.message);
          drainSource(srcIndex + 1);
        });
      }
      page();
    }

    drainSource(0);
  }

  /* ---------- Carousel ---------- */

  function init(section) {
    var track = section.querySelector('[data-track]');
    var controls = section.querySelector('[data-controls]');
    if (!track || !controls) return;

    var cards = track.querySelectorAll('.rvrn-posts-gallery-grid__card');
    if (!cards.length) return;

    var autoplayEnabled = section.getAttribute('data-autoplay') === 'true';
    var intervalMs = (parseInt(section.getAttribute('data-autoplay-interval'), 10) || 7) * 1000;

    // Desktop shows 3 cards per view; ≤820px shows ~1 (84vw)
    function perView() {
      return window.matchMedia('(max-width: 820px)').matches ? 1 : 3;
    }

    function pageCount() {
      return Math.max(1, Math.ceil(cards.length / perView()));
    }

    function pageStartLeft(pageIndex) {
      var idx = Math.min(pageIndex * perView(), cards.length - 1);
      var card = cards[idx];
      var trackRect = track.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      var left = track.scrollLeft + (cardRect.left - trackRect.left);
      // Mobile: cards snap to center (84vw, 8% peek on each side)
      if (perView() === 1) {
        var offset = (track.clientWidth - card.offsetWidth) / 2;
        return Math.max(0, left - offset);
      }
      return left;
    }

    function currentPage() {
      var scrollLeft = track.scrollLeft;
      var pages = pageCount();
      var nearest = 0;
      for (var i = 0; i < pages; i++) {
        var left = pageStartLeft(i);
        if (Math.abs(left - scrollLeft) < Math.abs(pageStartLeft(nearest) - scrollLeft)) {
          nearest = i;
        }
      }
      return nearest;
    }

    // ---- Dots ----
    var dots = [];
    function renderDots() {
      controls.innerHTML = '';
      dots = [];
      var count = pageCount();
      for (var i = 0; i < count; i++) {
        (function (index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'rvrn-posts-gallery-grid__dot';
          dot.setAttribute('aria-label', 'Go to page ' + (index + 1));
          dot.addEventListener('click', function () {
            go(index);
          });
          controls.appendChild(dot);
          dots.push(dot);
        })(i);
      }
    }

    function updateDots() {
      var active = currentPage();
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    function go(pageIndex) {
      var pages = pageCount();
      var idx = ((pageIndex % pages) + pages) % pages;
      track.scrollTo({
        left: pageStartLeft(idx),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    // ---- Autoplay ----
    var timer = null;
    function schedule() {
      if (!autoplayEnabled || REDUCED_MOTION.matches) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        go(currentPage() + 1);
        schedule();
      }, intervalMs);
    }

    function cancel() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    // User interaction resets the autoplay timer
    var scrollEndTimer = null;
    track.addEventListener('scroll', function () {
      updateDots();
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(schedule, 600);
    }, { passive: true });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        renderDots();
        updateDots();
        schedule();
      }, 200);
    });

    // ---- Init ----
    renderDots();
    updateDots();
    schedule();
  }

  function initAll() {
    document.querySelectorAll('.rvrn-posts-gallery-grid').forEach(function (section) {
      init(section);
      // Plan C: load remaining articles beyond the 50-item Liquid cap.
      loadAllViaStorefront(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
